import api from './api';

/**
 * Service for managing favorite bus stops
 */
const favoritesService = {
  /**
   * Get all favorite bus stops for the current user
   * @returns {Promise<Array>} Array of favorite bus stops
   */
  getFavorites: async () => {
    const response = await api.get('/favorites');
    return response.data;
  },

  /**
   * Get a specific favorite by ID
   * @param {number} id - Favorite ID
   * @returns {Promise<Object>} Favorite bus stop
   */
  getFavorite: async (id) => {
    const response = await api.get(`/favorites/${id}`);
    return response.data;
  },

  /**
   * Add a bus stop to favorites
   * @param {Object} favoriteData - { busStopId, label }
   * @returns {Promise<Object>} Created favorite
   */
  addFavorite: async (favoriteData) => {
    const response = await api.post('/favorites', favoriteData);
    return response.data;
  },

  /**
   * Update a favorite's label
   * @param {number} id - Favorite ID
   * @param {Object} updateData - { label }
   * @returns {Promise<Object>} Updated favorite
   */
  updateFavorite: async (id, updateData) => {
    const response = await api.put(`/favorites/${id}`, updateData);
    return response.data;
  },

  /**
   * Delete a favorite
   * @param {number} id - Favorite ID
   * @returns {Promise<void>}
   */
  deleteFavorite: async (id) => {
    await api.delete(`/favorites/${id}`);
  },

  /**
   * Update last accessed timestamp
   * @param {number} id - Favorite ID
   * @returns {Promise<void>}
   */
  updateLastAccessed: async (id) => {
    await api.post(`/favorites/${id}/access`);
  },

  /**
   * Check if a bus stop is in favorites
   * @param {number} busStopId - Bus stop ID
   * @param {Array} favorites - Array of user's favorites
   * @returns {Object|null} Favorite object if found, null otherwise
   */
  isFavorite: (busStopId, favorites) => {
    return favorites.find(fav => fav.busStopId === busStopId) || null;
  },

  /**
   * Reorder favorites
   * @param {Array} orderedIds - Array of favorite IDs in new order
   * @returns {Promise<Array>} Updated favorites
   */
  reorderFavorites: async (orderedIds) => {
    const response = await api.put('/favorites/reorder', { orderedIds });
    return response.data;
  }
};

export default favoritesService;
